import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertIcon } from '@/components/catalog/icons';
import { DocuSignCard } from '@/components/docusign/DocuSignCard';
import { ApiError, api } from '@/lib/api';
import { formatDateTime, orDash, pluralise } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { useXero } from '@/lib/xero-context';
import {
  XERO_ENTITIES,
  isWiredToXero,
  needsAttention,
  statusLabel,
  type XeroEntity,
  type XeroEntitySummary,
  type XeroSyncSummary,
} from '@/lib/xero';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { usePageLayout } from '@/lib/use-page-layout';

/*
 * Integrations —everything SEED EMERGE connects to outside itself.
 *
 * Xero is the only entry today, and the page is deliberately shaped as a list
 * of integrations rather than as "the Xero screen": a second one belongs here
 * as another panel, not as another route.
 *
 * NOTHING ON THIS PAGE TALKS TO XERO. Every call goes to our own API — the
 * client secret and the rotating refresh token cannot live in a browser, so
 * the token store, the sync worker and the rate limiter are all server-side.
 * See the header of `@/lib/xero`; it is structural, not a preference.
 *
 * **These endpoints may not exist yet.** The frontend ships ahead of the
 * backend here, so a 404 from `/xero/...` is the expected state on the way to
 * shipping and renders as a calm "not set up yet", never as a red crash. Only
 * a genuine failure gets the danger treatment.
 */

/*
 * Why the nav row for this page is NOT `adminOnly`.
 *
 * `adminOnly` is for surfaces the API refuses outright — the audit log 403s
 * anyone below admin even to read, so there is no partial page left to offer.
 * Nothing has verified that `/xero/connection` behaves that way, and hiding a
 * page on a guess removes a screen that works. So everyone may READ this, and
 * only the two write controls are gated.
 *
 * 🔴 **THIS STOPPED BORROWING `companies` ON 2026-09-02.** *SUPERSEDED:
 * "`companies.update` is the proxy for 'may change integration settings':
 * connecting or dropping Xero rewires where every company is pushed, so whoever
 * may not edit a company has no business rewiring that."*
 *
 * That sentence is a true statement about who should be excluded and says
 * nothing about who is thereby INCLUDED, which is where the cost was. Read the
 * other way round it granted **Xero and DocuSign connect and disconnect to
 * every person given `companies.update` so they could edit an account** — one
 * tickbox, two unrelated capabilities, and no way for a super-admin to separate
 * them. A borrowed slug is honest about the wrong thing, which is exactly why
 * nobody noticed: the proxy's stated reasoning reads as careful, and it is,
 * about the half it discusses.
 *
 * ⚠️ **`integrations` is CLIENT-ENFORCED ONLY.** No server guard consults it —
 * hiding these controls is not refusing the requests behind them, and nothing
 * here may be worded as though it were. The tier is unchanged: a write role may
 * still connect, narrowed now by a permission that is about this page alone.
 *
 * ⚠️ **Until the module is seeded, a non-admin write role loses these two
 * controls.** `/permissions/me` reports grants and never the catalogue, so an
 * absent `integrations.update` is indistinguishable from a denial and reads as
 * one. That is a narrowing in the safe direction — the failure is a missing
 * button on a control that rewires an accountant's ledger, not a stale grant —
 * and it heals when the sets are seeded. **The READ is untouched**: every role
 * still opens this page.
 *
 * Gated through `ability` and never `user.role` — see the header of
 * `@/lib/permissions` for why the role and `/permissions/me` disagree in both
 * directions.
 */
const INTEGRATION_MODULE = 'integrations' as const;

/** Registry order, so a fifth entity needs no change here. */
const ENTITY_ORDER = Object.keys(XERO_ENTITIES) as readonly XeroEntity[];

/*
 * ⛔ **`NOT_WIRED_YET`, `NOT_SENT_FROM_HERE` AND `NOT_SENT_UNKNOWN_REASON` WERE
 * DELETED — owner's helper-text ruling, 2026-09-05.** They existed only to
 * render one sentence per entity under "What is linked"; with that sentence gone
 * they were dead code, and dead code is how a claim survives past the thing it
 * described.
 *
 * **What they knew, kept because it is a fact about the CONTRACT and not about
 * the copy — there are THREE kinds of `false` in `XERO_ENTITIES.capabilities`,
 * and collapsing them is how a permanent limit gets logged as unbuilt work:**
 *
 * 1. **Structural** — `contacts`, all three. Xero has no record for a person on
 *    their own. Never flips. (Still rendered, as `CONTACTS_ARE_NOT_XERO_CONTACTS`
 *    on the panel and as Kind 1 below.)
 * 2. **Deliberate backend design** — `quotes.sync`, and `invoices.sync` for the
 *    identical reason: the push is inline, so `/xero/sync` is a `409
 *    XERO_PUSH_IS_INLINE`. Never flips, and must never be written as "coming
 *    later".
 * 3. **Built on the API, not adopted HERE** — `invoices.create` / `.link`. The
 *    only kind that can honestly change, and the only one whose reason lives on
 *    this side of the wire. The hold is the invoice itself: a duplicate is a
 *    second demand for money sent to a customer, which is not a mistake this app
 *    can detect or undo.
 *
 * ⚠️ **The correction those constants carried, so it is not made again:** the
 * invoices copy once said there was nothing to push them with. That was a claim
 * about the API and it went stale. Verified 2026-08-26 against
 * `emerge-backend-app` at `9d9576e` — `xero.registry.ts` carries a real
 * `invoices` entry, `POST /xero/create/invoices/{id}` answers `201`, and
 * `INLINE_PUSH_REASONS` keeps the queue worker off invoices. **The registry
 * booleans in `@/lib/xero` are all `false` for invoices deliberately, and that is
 * a HOLD ON THIS SIDE. Nothing on this page may say the backend has not built
 * it.**
 *
 * ⚠️ And if a per-entity sentence ever returns, the question is "wired to Xero at
 * all" — `isWiredToXero`, never one capability flag. A `sync` test gives the same
 * answer today and is wrong on purpose: quotes can be created in Xero while
 * `sync` stays permanently false, so it would list quotes and promise a push that
 * is never coming.
 */

/* --- shared bits --------------------------------------------------------- */

/**
 * A failure worth someone's attention. `[overflow-wrap:anywhere]` because an
 * API message can carry an unbroken id, and this page is tested at 320px.
 */
function ErrorBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" title={title}>
      <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
    </Notice>
  );
}

/** One labelled fact. A `dl` rather than a table — these are pairs, not rows. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-fg-subtle">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

/** The empty-state glyph, and the nav row's twin: a plug, pulled out. */
function PlugIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M12 22v-5" />
      <path d="M9 8V2" />
      <path d="M15 8V2" />
      <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
    </svg>
  );
}

/* --- the sync summary ---------------------------------------------------- */

interface SummaryState {
  data: XeroSyncSummary | null;
  loading: boolean;
  error: string | null;
  /** The API has no `/xero/summary` route yet. A 404, not a failure. */
  missing: boolean;
}

const IDLE_SUMMARY: SummaryState = { data: null, loading: true, error: null, missing: false };

/*
 * ⛔ **`cardNote` WAS DELETED — owner's helper-text ruling, 2026-09-05.** It
 * returned the footnote under one entity's counts, for an entity nothing here
 * pushes, and its three sentences were:
 *
 * - contacts: *"Contacts are never sent on their own — Xero has no record for a
 *   person by itself."*
 * - invoices: *"No screen here sends an invoice to Xero. That is done from an
 *   invoice's own page."*
 * - anything else: *"No control here sends these to Xero — the counts are what a
 *   link would cover."*
 *
 * ⚠️ **WHAT THE COUNTS MEAN IS NOW UNSAID, AND IT IS NOT THE SAME FOR ALL
 * THREE** — which is why this is recorded rather than dropped:
 *
 * - for CONTACTS, nothing was ever pushable and no link will ever cover them, so
 *   the numbers describe our own records and nothing else;
 * - for INVOICES, a count that is not "Not in Xero" is a REAL push of a REAL
 *   document — it was simply made somewhere else in SEED EMERGE. A reader who
 *   assumes those counts are hypothetical will go hunting for a document that
 *   exists.
 *
 * ⚠️ **The correction it carried, because it did INVERSE damage once and must
 * not again:** the invoice sentence used to attribute those pushes to
 * *"accepting a quote raises an invoice and sends it"* — a mechanism backend
 * release `20260829011913-5fbab06` removed. **The counts did not move; only the
 * explanation was wrong.** What accounts for them: an invoice is created by
 * converting a quote or billing its line items, and reaches Xero when somebody
 * pushes it from that invoice's own page. Neither route is on this screen.
 */

/**
 * One entity's counts.
 *
 * `needsAttention` leads because it is the only number anyone can act on: a
 * `failed` push was refused, and a `conflict` means both sides changed and
 * nothing will be written until a person chooses. Everything else is context.
 */
function EntityCard({ summary }: { summary: XeroEntitySummary }) {
  const meta = XERO_ENTITIES[summary.entity];
  const attention = needsAttention(summary);
  const headingId = useId();

  return (
    <Panel className="min-w-0" aria-labelledby={headingId}>
      <PanelBody className="flex h-full flex-col gap-3">
        <div className="min-w-0">
          {/*
           * The Xero side of the mapping is spelled out, never implied. Staff
           * otherwise read "Contacts" on both sides and assume our contacts
           * become Xero contacts — they do not; our COMPANIES do.
           */}
          <h3 id={headingId} className="m-0 text-sm font-semibold text-fg [overflow-wrap:anywhere]">
            {meta.plural} <span className="text-fg-subtle">&rarr;</span> Xero {meta.xeroName}s
          </h3>
          <p className="mt-0.5 text-xs text-fg-muted">
            {pluralise(summary.total, 'record')} in SEED EMERGE
          </p>
        </div>

        {attention > 0 ? (
          <Notice
            tone="danger"
            role="status"
            title={
              <>
                {pluralise(attention, 'record')} {attention === 1 ? 'needs' : 'need'} attention
              </>
            }
          >
            <p className="m-0">
              {summary.failed} refused by Xero, {summary.conflict} changed on both sides.
            </p>
          </Notice>
        ) : (
          <Badge tone="success" dot>
            Nothing needs attention
          </Badge>
        )}

        {/*
          These four counts describe the SAME six statuses the badge on a record
          describes, so they must not word them differently. They did: this card
          said "Synced" and "Queued" as literals while `statusLabel` had moved to
          "In Xero" and "Waiting", so one screen called a record one thing and
          the next screen called it another — with nothing to make them disagree
          out loud, because a literal string cannot go stale noisily.

          Reading `statusLabel` here is the fix, and it is the same rule this
          repo applies to the inclusion wording and the line description: one
          copy of a name, consulted, never a second copy that happens to match
          today. The remaining two are counts of a state with no single status
          behind them, so they stay literal and are worded not to collide.
        */}
        <dl className="mt-auto grid grid-cols-2 gap-x-3 gap-y-2">
          <Fact label={statusLabel('synced')}>{summary.synced}</Fact>
          <Fact label={statusLabel('pending')}>{summary.pending}</Fact>
          <Fact label="Not in Xero">{summary.unlinked}</Fact>
          <Fact label="Archived there">{summary.archived}</Fact>
        </dl>

        {/*
          * ⛔ **`cardNote` WAS REMOVED FROM THE CARD — owner's helper-text
          * ruling, 2026-09-05.** It rendered one per-entity sentence under the
          * counts, gated on `!create && !sync`.
          *
          * ⚠️ **The gate is the part worth keeping written down, because it is
          * subtle and it was got wrong once.** The claim was "nothing leaves
          * THIS APP for these records", so it read the two OUTBOUND
          * capabilities and not `isWiredToXero` — linking sends nothing.
          * `create` alone falsifies it: a quote is created in Xero, so its
          * counts describe real pushes even though `sync` is deliberately and
          * permanently false. Gating on `sync` would have printed the note over
          * quote counts that had genuinely been sent.
          *
          * ⚠️ And the sentence was per entity, NOT the shared one it replaced
          * (*"Nothing is sent for … yet. These counts are what a link would
          * cover, not what has been pushed"*) — both halves of which were wrong
          * by the time anybody read them: "yet" promised contacts a feature
          * that is structurally impossible, and "not what has been pushed" had
          * stopped being true of invoices.
          *
          * **If a per-entity sentence is ever wanted here again, read the two
          * outbound capabilities, not `isWiredToXero`, and keep it per entity.**
          */}
      </PanelBody>
    </Panel>
  );
}

function EntityCardSkeleton() {
  return (
    <Panel className="min-w-0">
      <PanelBody className="flex flex-col gap-3">
        <Skeleton className="h-4 w-44" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-full" />
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-7 w-16" />
        </div>
      </PanelBody>
    </Panel>
  );
}

/* --- page ---------------------------------------------------------------- */

export function IntegrationsPage() {
  const { ability } = useAuth();
  const {
    connection,
    loading: connectionLoading,
    error: connectionError,
    notInstalled,
    reload,
  } = useXero();

  const [summary, setSummary] = useState<SummaryState>(IDLE_SUMMARY);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const connectionHeadingId = useId();
  const summaryHeadingId = useId();

  /*
   * The per-person layout. Defaults reproduce the page as it was: Xero, then
   * "What is linked", then DocuSign, all full width. Ids are stored per user —
   * never rename one.
   */
  const layout = usePageLayout('integrations', [
    { id: 'xero', label: 'Xero' },
    { id: 'linked', label: 'What is linked' },
    { id: 'docusign', label: 'DocuSign' },
  ]);

  const canManage = ability.canUpdate(INTEGRATION_MODULE);
  const manageDenial = denialReason(ability, 'update', INTEGRATION_MODULE);

  /*
   * The READ gate, added 2026-09-04 — and it is a SECOND question from
   * `canManage` above, not a stricter version of it. `read` decides whether this
   * screen exists for the reader; `update` decides whether they may connect or
   * disconnect Xero, which is a real capability against a real endpoint. The
   * `operations` set denies `integrations` outright, and until this line nothing
   * asked, so the page rendered in full for a reader an administrator had hidden
   * it from.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. See `can()` in
   * `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /xero/connection`
   * answers whoever its own guard allows, unchanged by this, so nothing here may
   * be worded as though the connection were protected by the permission set.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook.
   */
  const canReadModule = ability.canRead(INTEGRATION_MODULE);

  /*
   * Nothing here sets `loading` on the way IN, only on the way out. The state
   * starts loading and the effect below runs at most once per mount, so a
   * synchronous `setState` in the effect would only start a second render to
   * write a value that is already there.
   */
  const loadSummary = useCallback((signal: AbortSignal) => {
    return api
      .xeroSyncSummary(signal)
      .then((data) => {
        if (signal.aborted) return;
        setSummary({ data, loading: false, error: null, missing: false });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * The same split as the connection read: a 404 means the route does
         * not exist yet and gets the calm state, while a 401 is left entirely
         * to `api.ts`, which already attempts one refresh and then ends the
         * session — reporting it here would put a second, contradictory
         * message on screen midway through signing out.
         */
        if (caught instanceof ApiError && caught.status === 404) {
          setSummary({ data: null, loading: false, error: null, missing: true });
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setSummary({
          data: null,
          loading: false,
          error: caught instanceof Error ? caught.message : 'Could not read the Xero sync summary.',
          missing: false,
        });
      })
      .finally(() => {
        if (signal.aborted) return;
        setSummary((current) => ({ ...current, loading: false }));
      });
  }, []);

  useEffect(() => {
    /*
     * Nothing to ask for until the connection read settles, and nothing worth
     * asking once it has reported the routes absent — a second guaranteed 404
     * buys nothing and spends a request against the rate limit.
     */
    if (connectionLoading || notInstalled) return;
    const controller = new AbortController();
    void loadSummary(controller.signal);
    return () => controller.abort();
  }, [connectionLoading, notInstalled, loadSummary]);

  function connect() {
    /*
     * Ask for the URL over XHR, then navigate. Sending the browser straight
     * at `/xero/connect` would carry no `Authorization` header — a full-page
     * navigation never does — and every endpoint on this API is guarded, so
     * it would simply 401. The request below carries the token normally; the
     * navigation that follows goes to Xero, which does not want it.
     *
     * No `finally` resetting the flag on success: the page is being replaced,
     * and re-enabling the button first only invites a second click that would
     * mint a second `state` nonce and invalidate the first.
     */
    setConnecting(true);
    setConnectError(null);
    api
      .beginXeroConnect()
      .then(({ authorizeUrl }) => {
        window.location.href = authorizeUrl;
      })
      .catch((caught: unknown) => {
        setConnecting(false);
        setConnectError(
          caught instanceof Error ? caught.message : "Could not start the Xero connection.",
        );
      });
  }

  function disconnect() {
    setDisconnecting(true);
    setDisconnectError(null);
    api
      .disconnectXero()
      .then(() => {
        setConfirmOpen(false);
        /*
         * The connection is read once for the whole app and every panel that
         * offers an "Open in Xero" link reads that copy, so the shared one is
         * what has to be refreshed rather than a local flag. The counts are
         * untouched, because the links are KEPT.
         */
        reload();
      })
      .catch((caught: unknown) => {
        /*
         * A 403 here means the role changed under a token that lasts over five
         * days, so the hidden control was an optimisation rather than a
         * guarantee. It carries no `details`, unlike a 422, so it stays a
         * banner and never becomes a per-field message.
         */
        setDisconnectError(
          caught instanceof ApiError && caught.status === 403
            ? (manageDenial ??
              'The API refused this with a 403. Your role may have changed since you signed in.')
            : caught instanceof Error
              ? caught.message
              : 'Could not disconnect Xero.',
        );
      })
      .finally(() => setDisconnecting(false));
  }

  /*
   * A failed consent bounces back here as `?xeroError=<code>`. The code is
   * reduced to [a-z0-9_] and capped server-side before it reaches the URL — it
   * arrives from Xero and is untrusted — and no description is forwarded,
   * because a redirect target lands in browser history and Referer headers.
   *
   * Read with useSearchParams() and NO argument: its first parameter is a
   * default init rather than options, so passing {replace:true} there would put
   * a literal replace=true into the query string.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const xeroError = searchParams.get('xeroError');
  const dismissXeroError = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('xeroError');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Admin" title="Integrations" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Integrations"
        actions={<LayoutToolbar layout={layout} />}
      />

      {xeroError && (
        <Notice
          tone="danger"
          role="alert"
          className="mb-4"
          action={
            <Button variant="ghost" onClick={dismissXeroError}>
              Dismiss
            </Button>
          }
        >
          <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{xeroErrorMessage(xeroError)}</p>
        </Notice>
      )}

      <LayoutGrid
        layout={layout}
        render={{
          /*
           * The backend ships its `/xero` half separately, so "no such route" is a
           * normal state on the way there and reads as one. "What is linked" is
           * skipped (its render returns null): it would only produce a second 404
           * saying the same thing.
           */
          xero: () =>
            notInstalled ? (
        <Panel>
          <PanelBody density="comfortable">
            <EmptyState
              icon={<PlugIcon />}
              title="Xero is not set up on the API yet"
              description="The API has no Xero endpoints to answer this yet."
            />
          </PanelBody>
        </Panel>
      ) : (
        <>
          {/*
           * ⛔ **THE LAPSING-CONNECTION WARNING WAS REMOVED — owner's
           * helper-text ruling, 2026-09-05.** It read: *"This connection lapses
           * on {date}. Someone has to authorise it again by hand. {name} set it
           * up."*, gated on `connectionExpiringSoon`.
           *
           * ⚠️ **Its reasoning was never refuted and is recorded here rather
           * than deleted with it:** Xero's refresh token is renewed on every use
           * and dies after 60 days idle, and once it has, the only fix is a
           * person re-authorising from scratch. The warning existed to surface
           * that while it was still one click, rather than have it discovered on
           * the morning everything stops.
           *
           * **The cost is real and is accepted, not overlooked:** an expiring
           * connection is now visible only as the "Connected on" fact below,
           * which nobody reads as a deadline. If this is ever wanted back, it is
           * a warning about an integration going dark, not helper text — put it
           * to the owner rather than restoring it quietly.
           */}
          <Panel aria-labelledby={connectionHeadingId}>
            <PanelHeader
              id={connectionHeadingId}
              title="Xero"
              actions={
                connection.connected ? (
                  <Button
                    variant="secondary"
                    disabled={!canManage || connectionLoading}
                    title={manageDenial ?? undefined}
                    onClick={() => {
                      setDisconnectError(null);
                      setConfirmOpen(true);
                    }}
                  >
                    Disconnect
                  </Button>
                ) : null
              }
            />
            <PanelBody className="flex flex-col gap-3">
              {connectionError && (
                <ErrorBlock title="Couldn’t read the Xero connection.">
                  {connectionError}
                </ErrorBlock>
              )}

              {disconnectError && !confirmOpen && (
                <ErrorBlock title="Couldn’t disconnect.">{disconnectError}</ErrorBlock>
              )}

              {connectionLoading ? (
                <div className="flex flex-col gap-3">
                  <Skeleton className="h-6 w-56" />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                </div>
              ) : connection.connected ? (
                <>
                  {/*
                   * The organisation name is the loudest thing on this panel,
                   * on purpose. One Xero login can grant several
                   * organisations, and pushing SEED's records into the wrong
                   * one is silent — undoing it means deleting records inside
                   * an accounting system. It is never left implicit.
                   */}
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="m-0 min-w-0 text-lg font-semibold text-fg [overflow-wrap:anywhere]">
                      {orDash(connection.tenantName)}
                    </p>
                    <Badge tone="success" dot>
                      Connected
                    </Badge>
                  </div>

                  <dl className="grid gap-3 sm:grid-cols-2">
                    <Fact label="Xero organisation ID">
                      {connection.tenantId ? (
                        <span className="font-mono text-xs">{connection.tenantId}</span>
                      ) : (
                        '—'
                      )}
                    </Fact>
                    {/*
                     * Xero's base currency, and a fact about the organisation
                     * only. Our own records carry a currency each and are
                     * never converted, so nothing on a quote or an invoice is
                     * expressed in this one.
                     */}
                    <Fact label="Base currency in Xero">{orDash(connection.baseCurrency)}</Fact>
                    <Fact label="Connected by">
                      {orDash(connection.connectedBy?.name ?? null)}
                    </Fact>
                    <Fact label="Connected on">
                      {connection.connectedAt ? formatDateTime(connection.connectedAt) : '—'}
                    </Fact>
                  </dl>
                </>
              ) : !connection.configured ? (
                /*
                 * Not the same as "not connected", and the difference decides
                 * whether there is anything to offer. `configured: false` means
                 * the SERVER holds no Xero credentials — nobody signed in here
                 * can fix that, so a "Connect Xero" button would only produce an
                 * error. It is an operations task, so it says so and stops.
                 */
                <EmptyState
                  icon={<PlugIcon />}
                  title="Xero is not set up on the server"
                  description="No Xero credentials on the server. Set them in the server’s env file."
                />
              ) : (
                <EmptyState
                  icon={<PlugIcon />}
                  title="Xero is not connected"
                  description="Nothing is written to Xero until someone links a record."
                  action={
                    <div className="flex flex-col items-center gap-2">
                      <Button
                        variant="primary"
                        disabled={!canManage || connecting}
                        title={manageDenial ?? undefined}
                        onClick={connect}
                      >
                        {connecting ? 'Opening Xero…' : 'Connect Xero'}
                      </Button>
                      {/*
                        * A failure here happens before the browser leaves, so
                        * it is reported in place. A 403 carries no `details`,
                        * which is why this is a plain banner rather than
                        * anything field-shaped.
                        */}
                      {connectError && (
                        <p className="m-0 text-xs text-danger">{connectError}</p>
                      )}
                      {/*
                       * The reason is text as well as a `title`: a disabled
                       * button has `pointer-events: none`, so its tooltip
                       * never appears for the person it is meant for.
                       */}
                      {manageDenial && <p className="m-0 text-xs text-fg-subtle">{manageDenial}</p>}
                    </div>
                  }
                />
              )}
            </PanelBody>
          </Panel>
        </>
            ),
          linked: () =>
            notInstalled ? null : (
          <Panel aria-labelledby={summaryHeadingId}>
            <PanelHeader id={summaryHeadingId} title="What is linked" />
            <PanelBody className="flex flex-col gap-4">
              {/*
               * ⛔ **KIND 3 WAS REMOVED — owner's helper-text ruling,
               * 2026-09-05.** It mapped `NOT_WIRED_YET` to one sentence per
               * entity: *"Nothing here sends {plural} to Xero."* followed by
               * `NOT_SENT_FROM_HERE[entity]`, or `NOT_SENT_UNKNOWN_REASON` for
               * an entity with no reason written down.
               *
               * ⚠️ **THE OTHER TWO KINDS STAY, AND THE DISTINCTION IS WHY.**
               * They were three because the reasons were three — structural, a
               * deliberate design decision, and a hold on THIS side over
               * something the API already does — and only the third was
               * describing an absence. Kind 2 (the DRAFT boundary) and Kind 1
               * (a person has no record in Xero) each name a way a document
               * reaching an accountant's system behaves differently from what
               * the reader would assume, which is the one category that
               * survives the ruling.
               *
               * ⚠️ **Do not collapse the two survivors into one sentence** —
               * that was the original argument for keeping them separate, and
               * it is unchanged: one shared sentence over the top of them is
               * how the third gets logged as the second, or the first as
               * either. Each still reads the registry in `@/lib/xero`, so a
               * capability flipping there silences the matching sentence on its
               * own.
               */}
              <div className="flex items-start gap-2 text-xs text-fg-muted">
                <span className="text-fg-subtle">
                  <AlertIcon />
                </span>
                <div className="flex min-w-0 max-w-3xl flex-col gap-1.5">
                  {/*
                   * Kind 2, and the one most likely to be mistaken for a gap.
                   *
                   * ⚠️ This paragraph said "Later edits here are not sent after
                   * it … That is the design, not a step still to come." It was
                   * true when written and is now FALSE: saving a linked quote
                   * or invoice re-sends it, because the backend's
                   * `POST /xero/create/{entity}/{id}` has always been
                   * create-OR-update and this app simply never called the
                   * update half.
                   *
                   * Note what did NOT save it: the gate below is
                   * `capabilities.sync`, which is `false` and STAYS false —
                   * that flag is about the background worker, which really does
                   * refuse quote and invoice jobs. So the gate went on being
                   * satisfied while the sentence it guarded stopped being true.
                   * A flag that tracks a NEIGHBOURING fact is not a guard; it
                   * is a coincidence with a comment on it.
                   *
                   * The dividing line is Xero's own status, not ours, which is
                   * why the sentence now names DRAFT rather than "once".
                   */}
                  {!XERO_ENTITIES.quotes.capabilities.sync && (
                    <p className="m-0 text-fg-subtle">
                      Edits keep being sent only while Xero&rsquo;s copy is still a draft. Once it
                      is sent from Xero, it is edited in Xero.
                    </p>
                  )}

                  {/* Kind 1. Permanent, and the only one nothing can flip. */}
                  {!isWiredToXero('contacts') && (
                    <p className="m-0 text-fg-subtle">
                      Xero has no record for a person on their own, so a contact travels as part of
                      its company or not at all.
                    </p>
                  )}
                </div>
              </div>

              {summary.error && (
                <ErrorBlock title="Couldn’t load the sync summary.">{summary.error}</ErrorBlock>
              )}

              {summary.missing && (
                <EmptyState
                  icon={<PlugIcon />}
                  title="The API has no sync summary yet"
                  description="These counts appear once the backend ships its Xero endpoints."
                />
              )}

              {summary.loading && !summary.data ? (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {ENTITY_ORDER.map((entity) => (
                    <EntityCardSkeleton key={entity} />
                  ))}
                </div>
              ) : summary.data ? (
                <>
                  {/*
                   * ⛔ **THE OLDEST-QUEUED LINE WAS REMOVED — owner's
                   * helper-text ruling, 2026-09-05.** Its three branches were:
                   * *"Oldest record still queued: {date}. If this date stops
                   * moving forward, the sync worker has stopped."*, *"Nothing is
                   * queued — every linked record has been sent."*, and, for an
                   * ABSENT key, *"The queued figure is not reported by the API,
                   * so a stalled worker would not show here."*
                   *
                   * ⚠️ **Two things it was carrying, both now unsaid, both
                   * deliberate:**
                   *
                   * 1. The oldest queue entry was the one figure on this page
                   *    that said whether the push worker is alive at all. A date
                   *    that stops moving while the queue is non-empty means
                   *    pushes have stopped, and that is invisible in every
                   *    per-entity count beside it.
                   * 2. It had THREE branches, not two, because `null` is a real
                   *    all-clear and an ABSENT key is not — an all-clear derived
                   *    from missing data reads as reassurance at exactly the
                   *    moment the worker has died.
                   *
                   * **`summary.data.oldestPendingAt` is untouched on the wire.**
                   * Restoring this is a render, not a contract change — but it
                   * is a monitoring statement rather than helper text, so put it
                   * to the owner rather than adding it back quietly, and keep
                   * all three branches if it comes back.
                   */}
                  {summary.data.entities.length === 0 ? (
                    <EmptyState
                      icon={<PlugIcon />}
                      title="No entities are set up to sync"
                      description="The API reported no syncable entities."
                    />
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {/*
                       * Rendered in the order received. An entity the app has
                       * no registry entry for is skipped rather than drawn
                       * with `undefined` where its label should be.
                       */}
                      {summary.data.entities
                        .filter((entry) => entry.entity in XERO_ENTITIES)
                        .map((entry) => (
                          <EntityCard key={entry.entity} summary={entry} />
                        ))}
                    </div>
                  )}
                </>
              ) : null}
            </PanelBody>
          </Panel>
            ),
          /*
       * DocuSign, a sibling of the Xero panel and deliberately OUTSIDE the
       * `notInstalled` branch above — that flag is about the `/xero/*` routes
       * being absent, and the two integrations fail independently. The card
       * reads its own connection and carries its own three states.
       *
       * ⚠️ **This comment said "Connection only: sending envelopes for signature
       * is not built yet". That is out of date** — the card now also reads
       * `GET /docusign/envelopes?outstanding=true` and reports how many quotes
       * are waiting on a customer's signature, linking through to each one's own
       * quote. Sending is done from a quote, not from here: this page is a list
       * of integrations, and a send screen needs a quote in front of it.
       *
       * ⚠️ **THERE IS NO CONNECT OR DISCONNECT BUTTON HERE, AND ONE MUST NOT BE
       * ADDED.** DocuSign is a JWT Bearer grant: the backend signs an assertion
       * with a private RSA key and exchanges it for a token. There is no
       * handshake to start, no callback to come back to, no connection row and
       * no token to revoke — so there is nothing for a button to do. Xero is the
       * opposite and genuinely needs `POST /xero/connect` before the browser may
       * leave; do not copy that panel's shape across. The one link the card may
       * offer is DocuSign's own consent URL, when the connection reports one,
       * and that is what it offers.
       *
       * The card owns its own not-legally-binding warning, and the rule behind
       * it REVERSED on 2026-08-31 — *SUPERSEDED: "on the rule 'warn unless
       * PROVABLY production' — a missing response must warn, because that is the
       * moment we know least. The note below is the other half: that this state
       * is known and accepted rather than unnoticed."* It now warns only on a
       * connection it actually read that is not a production one; see
       * `signaturesNotBinding()`.
       *
       * ⛔ **AND THE NOTE BELOW IT IS DELETED — owner's instruction, 2026-08-31.**
       * It read: *"The sandbox above is known, and the decision is to leave it.
       * Go Live has been submitted and the warning clears when it is approved.
       * Until then a signature worth nothing still accepts the quote it was sent
       * for."* **Go Live is approved and DocuSign is on production**, so every
       * clause of it was false — a pending submission that has landed, a warning
       * described as still standing, and an "until then" with no then left. Its
       * explanatory comment went with it rather than being left to describe a
       * paragraph that is not there.
       *
       * **Do not restore it as an oversight.** If the environment ever goes back
       * to a sandbox, the card says so on its own — which is the whole reason
       * that warning is generated from the connection and not written here.
       */
          docusign: () => <DocuSignCard canManage={canManage} manageDenial={manageDenial} />,
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={disconnect}
        title="Disconnect Xero?"
        confirmLabel="Disconnect"
        loading={disconnecting}
        error={disconnectError}
        description={
          <>
            <p className="m-0">
              SEED EMERGE stops sending anything to{' '}
              <span className="font-medium text-fg">{orDash(connection.tenantName)}</span>, and the
              token is revoked in Xero.
            </p>
            <p className="m-0 mt-2">
              <span className="font-medium text-fg">Existing links are kept</span> &mdash;
              reconnecting the same organisation picks up where this left off. Connecting a{' '}
              <em>different</em> one invalidates them.
            </p>
            <p className="m-0 mt-2">Nothing inside Xero is changed or deleted.</p>
          </>
        }
      />
    </>
  );
}

/**
 * A callback error code as a sentence someone can act on.
 *
 * Unknown codes are shown verbatim rather than swallowed: Xero passes its own
 * through, and an unrecognised one is still more use to whoever reads the
 * screen than "something went wrong".
 *
 * **This cannot cover every failed connect, and the copy must not imply it
 * does.** A consent rejected at the AUTHORIZE step — `invalid_scope` was
 * exactly this — never reaches our callback at all, so no code is produced and
 * the person is left on a Xero-branded error page instead. Returning here with
 * no code means we do not know what happened, which is why nothing on this page
 * treats the absence of an error as evidence of success.
 */
function xeroErrorMessage(code: string): string {
  switch (code) {
    case 'access_denied':
      return 'Cancelled at Xero, so nothing changed. Start again when you are ready.';
    case 'missing_parameters':
      return 'Xero sent us back without the details needed. Start the connection again.';
    case 'exchange_failed':
      return 'Could not complete the handshake — the sign-in sat too long or was already used. Start it again.';
    case 'server_error':
      return 'The reason is in the server log. Ask whoever runs the API to look, with the time you tried.';
    default:
      return `Xero refused the connection and gave the reason “${code}”. Nothing was changed.`;
  }
}
